import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { submitFeedback, CreateFeedbackData } from '../lib/api';
import { getCurrentLocale } from '../i18n';

interface FormErrors {
  title?: string;
  description?: string;
  email?: string;
}

interface DeviceInfo {
  platform: string;
  appVersion: string;
  deviceModel: string;
  osVersion: string;
}

interface Props {
  onSuccess: (refCode: string) => void;
}

// Which tab to open first. Native apps pass `type=bug` (问题反馈) or `type=suggestion` (建议)
// when they deep-link into a specific section; anything else keeps the default (bug).
function getInitialTypeFromURL(): 'bug' | 'suggestion' {
  const type = new URLSearchParams(window.location.search).get('type');
  return type === 'suggestion' ? 'suggestion' : 'bug';
}

// Parse device info from URL params
// Usage: /feedback?platform=ios&app_version=1.2.3&device_model=iPhone15,2&os_version=17.2&type=bug
function getDeviceInfoFromURL(): DeviceInfo {
  const params = new URLSearchParams(window.location.search);
  return {
    platform: params.get('platform') || '',
    appVersion: params.get('app_version') || '',
    deviceModel: params.get('device_model') || '',
    osVersion: params.get('os_version') || ''
  };
}

export function FeedbackForm({ onSuccess }: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState<'bug' | 'suggestion'>(getInitialTypeFromURL);
  const [showMore, setShowMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Device info from URL (auto-filled by native apps)
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    platform: '',
    appVersion: '',
    deviceModel: '',
    osVersion: ''
  });

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('');
  const [severity, setSeverity] = useState('');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [scenario, setScenario] = useState('');

  // File upload
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Read device info from URL on mount
  useEffect(() => {
    const info = getDeviceInfoFromURL();
    setDeviceInfo(info);
  }, []);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    const maxFiles = 3;
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    const totalFiles = files.length + selectedFiles.length;
    const filesToAdd = Math.min(selectedFiles.length, maxFiles - files.length);

    for (let i = 0; i < filesToAdd; i++) {
      const file = selectedFiles[i];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError(t('error.invalidFileType'));
        continue;
      }
      if (file.size > maxSize) {
        setSubmitError(t('error.fileTooLarge'));
        continue;
      }
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    if (totalFiles > maxFiles) {
      setSubmitError(t('error.tooManyFiles'));
    }

    setFiles(prev => [...prev, ...newFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  }, [files.length, t]);

  const removeFile = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!title || title.length < 2 || title.length > 80) {
      newErrors.title = t('error.titleLength');
    }

    if (!description || description.length < 10 || description.length > 2000) {
      newErrors.description = t('error.descriptionLength');
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('error.invalidEmail');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const data: CreateFeedbackData = {
        type,
        locale: getCurrentLocale(),
        title,
        description,
        email: email || undefined,
        consent: true, // Always true now
        platform: deviceInfo.platform || undefined,
        app_version: deviceInfo.appVersion || undefined,
        device_model: deviceInfo.deviceModel || undefined,
        os_version: deviceInfo.osVersion || undefined,
        ...(type === 'bug' && {
          frequency: frequency || undefined,
          severity: severity || undefined,
          steps: steps || undefined,
          expected: expected || undefined,
          actual: actual || undefined
        }),
        ...(type === 'suggestion' && {
          category: category || undefined,
          priority: priority || undefined,
          scenario: scenario || undefined
        })
      };

      const response = await submitFeedback(data, files);

      if (response.ok && response.data) {
        onSuccess(response.data.ref_code);
      } else {
        setSubmitError(response.error || t('error.submitFailed'));
      }
    } catch (error) {
      setSubmitError(t('error.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if device info was provided via URL
  const hasDeviceInfo = deviceInfo.platform || deviceInfo.appVersion || deviceInfo.deviceModel || deviceInfo.osVersion;

  return (
    <form onSubmit={handleSubmit}>
      {/* Device Info Banner - shown when auto-filled */}
      {hasDeviceInfo && (
        <div className="device-info-banner">
          <span className="device-info-label">{deviceInfo.platform}</span>
          {deviceInfo.appVersion && <span className="device-info-label">v{deviceInfo.appVersion}</span>}
          {deviceInfo.deviceModel && <span className="device-info-label">{deviceInfo.deviceModel}</span>}
          {deviceInfo.osVersion && <span className="device-info-label">{deviceInfo.osVersion}</span>}
        </div>
      )}

      {/* Urgent Feedback Tip */}
      <div className="urgent-tip">
        <svg className="urgent-tip-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18h6" /><path d="M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V17h6v-.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" />
        </svg>
        <span>
          {t('urgentTip.text')}{' '}
          <a href={`mailto:${t('urgentTip.email')}`} className="urgent-tip-email">
            {t('urgentTip.email')}
          </a>
          {' '}{t('urgentTip.suffix')}
        </span>
      </div>

      {/* Type Tabs */}
      <div className="type-tabs">
        <button
          type="button"
          className={`type-tab ${type === 'bug' ? 'active' : ''}`}
          onClick={() => setType('bug')}
        >
          {t('type.bug')}
        </button>
        <button
          type="button"
          className={`type-tab ${type === 'suggestion' ? 'active' : ''}`}
          onClick={() => setType('suggestion')}
        >
          {t('type.suggestion')}
        </button>
      </div>

      {/* Title */}
      <div className="form-group">
        <label className="form-label required">{t('field.title.label')}</label>
        <input
          type="text"
          className={`form-input ${errors.title ? 'error' : ''}`}
          placeholder={t('field.title.placeholder')}
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={80}
        />
        {errors.title && <div className="form-error">{errors.title}</div>}
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="form-label required">{t('field.description.label')}</label>
        <textarea
          className={`form-textarea ${errors.description ? 'error' : ''}`}
          placeholder={t('field.description.placeholder')}
          value={description}
          onChange={e => setDescription(e.target.value)}
          maxLength={2000}
        />
        {errors.description && <div className="form-error">{errors.description}</div>}
      </div>

      {/* Bug-specific fields */}
      {type === 'bug' && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('field.frequency.label')}</label>
              <select
                className="form-select"
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
              >
                <option value="">{t('admin.filters.all')}</option>
                <option value="always">{t('field.frequency.always')}</option>
                <option value="sometimes">{t('field.frequency.sometimes')}</option>
                <option value="once">{t('field.frequency.once')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('field.severity.label')}</label>
              <select
                className="form-select"
                value={severity}
                onChange={e => setSeverity(e.target.value)}
              >
                <option value="">{t('admin.filters.all')}</option>
                <option value="blocker">{t('field.severity.blocker')}</option>
                <option value="major">{t('field.severity.major')}</option>
                <option value="minor">{t('field.severity.minor')}</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* Suggestion-specific fields */}
      {type === 'suggestion' && (
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('field.category.label')}</label>
            <select
              className="form-select"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <option value="">{t('admin.filters.all')}</option>
              <option value="feature">{t('field.category.feature')}</option>
              <option value="ux">{t('field.category.ux')}</option>
              <option value="performance">{t('field.category.performance')}</option>
              <option value="content">{t('field.category.content')}</option>
              <option value="other">{t('field.category.other')}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('field.priority.label')}</label>
            <select
              className="form-select"
              value={priority}
              onChange={e => setPriority(e.target.value)}
            >
              <option value="">{t('admin.filters.all')}</option>
              <option value="nice_to_have">{t('field.priority.nice_to_have')}</option>
              <option value="important">{t('field.priority.important')}</option>
              <option value="urgent">{t('field.priority.urgent')}</option>
            </select>
          </div>
        </div>
      )}

      {/* More Options Toggle */}
      <button
        type="button"
        className={`more-options-toggle ${showMore ? 'expanded' : ''}`}
        onClick={() => setShowMore(!showMore)}
      >
        {showMore ? t('moreOptions.hide') : t('moreOptions.show')}
      </button>

      {/* More Options */}
      {showMore && (
        <>
          {/* Bug: Steps, Expected, Actual */}
          {type === 'bug' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('field.steps.label')}</label>
                <textarea
                  className="form-textarea"
                  placeholder={t('field.steps.placeholder')}
                  value={steps}
                  onChange={e => setSteps(e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('field.expected.label')}</label>
                  <textarea
                    className="form-textarea"
                    placeholder={t('field.expected.placeholder')}
                    value={expected}
                    onChange={e => setExpected(e.target.value)}
                    style={{ minHeight: '80px' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('field.actual.label')}</label>
                  <textarea
                    className="form-textarea"
                    placeholder={t('field.actual.placeholder')}
                    value={actual}
                    onChange={e => setActual(e.target.value)}
                    style={{ minHeight: '80px' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Suggestion: Scenario */}
          {type === 'suggestion' && (
            <div className="form-group">
              <label className="form-label">{t('field.scenario.label')}</label>
              <textarea
                className="form-textarea"
                placeholder={t('field.scenario.placeholder')}
                value={scenario}
                onChange={e => setScenario(e.target.value)}
              />
            </div>
          )}
        </>
      )}

      {/* File Upload */}
      <div className="form-group">
        <label className="form-label">{t('field.attachments.label')}</label>
        <div
          className="file-upload"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <div className="file-upload-text">{t('field.attachments.dragDrop')}</div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          onChange={e => handleFileSelect(e.target.files)}
        />
        <div className="form-hint">{t('field.attachments.hint')}</div>

        {previews.length > 0 && (
          <div className="file-preview">
            {previews.map((preview, index) => (
              <div key={index} className="file-preview-item">
                <img src={preview} alt="" />
                <button
                  type="button"
                  className="file-preview-remove"
                  onClick={() => removeFile(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email */}
      <div className="form-group">
        <label className="form-label">{t('field.email.label')}</label>
        <input
          type="email"
          className={`form-input ${errors.email ? 'error' : ''}`}
          placeholder={t('field.email.placeholder')}
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <div className="form-hint">{t('field.email.hint')}</div>
        {errors.email && <div className="form-error">{errors.email}</div>}
      </div>

      {/* Submit Error */}
      {submitError && (
        <div className="form-group">
          <div className="form-error">{submitError}</div>
        </div>
      )}

      {/* Submit Button */}
      <button type="submit" className="submit-btn" disabled={isSubmitting}>
        {isSubmitting ? t('submit.submitting') : t('submit.button')}
      </button>
    </form>
  );
}
